import React, { useMemo, useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, AreaChart, Area
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { FaHeartbeat, FaThermometerHalf, FaTint, FaChartLine, FaTable, FaPrint, FaFilePdf } from 'react-icons/fa';
import { exportTPRChartPDF } from './TPRChartPDF';
import { getHeaderHtml } from '../../../../util/headerUtil';

const VitalsTrendChart = ({ treatments = [], historicTreatments = [], patientData = {}, authCtx = {} }) => {
    const { t } = useTranslation();
    const hospitalData = authCtx?.hospitalData;
    const [activeMetric, setActiveMetric] = useState('tpr'); // tpr, cardio, fever, bp

    // Combine and format the data
    const data = useMemo(() => {
        const combined = [...historicTreatments, ...treatments].map(t => {
            // Handle the data format differences between historic (from API) and new (from form)
            let dateStr = t.date || "";
            let timeStr = t.time || "";

            if (t.vitalDateTime) {
                if (t.vitalDateTime.includes('T')) [dateStr, timeStr] = t.vitalDateTime.split('T');
                else if (t.vitalDateTime.includes(' ')) [dateStr, timeStr] = t.vitalDateTime.split(' ');
                else dateStr = t.vitalDateTime;
            }

            const timestamp = new Date(`${dateStr} ${timeStr}`).getTime();
            const displayTime = timeStr ? timeStr.slice(0, 5) : "";
            const displayDate = dateStr ? dateStr.slice(0, 10) : ""; // YYYY-MM-DD
            const shortDate = dateStr ? dateStr.slice(5) : ""; // MM-DD

            return {
                timestamp,
                date: displayDate,
                formattedTime: `${shortDate} ${displayTime}`,
                time: displayTime,
                bpSystolic: Number(t.bpSystolic) || null,
                bpDiastolic: Number(t.bpDiastolic) || null,
                temp: Number(t.temperature || t.temp) || null,
                pulse: Number(t.pulse) || null,
                spo2: Number(t.spo2) || null,
                weight: Number(t.weight) || null,
                rr: t.option1 || t.rr || "-",
                notes: t.notes || t.remarks || "-",
                details: t.details || "N/A"
            };
        });

        // Sort by chronological order
        return combined.sort((a, b) => a.timestamp - b.timestamp);
    }, [treatments, historicTreatments]);

    // if (data.length < 1) return null; 

    const handlePrint = () => {
        const printContent = document.getElementById('tpr-printable-section');
        if (!printContent) return;

        // Use a hidden iframe for printing to avoid opening a new window
        let iframe = document.getElementById('tpr-print-iframe');
        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.id = 'tpr-print-iframe';
            iframe.style.position = 'absolute';
            iframe.style.width = '0px';
            iframe.style.height = '0px';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
        }

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
            <html>
                <head>
                    <title>TPR Chart - ${patientData.name || ""}</title>
                    <style>
                        @page { margin: 0; size: A4 portrait; }
                        body { 
                            padding: 0; 
                            margin: 0; 
                            font-family: Arial, sans-serif; 
                            -webkit-print-color-adjust: exact; 
                        }
                        .page-wrapper {
                            display: flex;
                            flex-direction: column;
                            min-height: 290mm; /* Near A4 height */
                            padding: 0;
                            box-sizing: border-box;
                        }
                        .print-container { 
                            padding: 10mm; 
                            flex-grow: 1;
                            display: flex;
                            flex-direction: column;
                        }
                        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                        th, td { border: 1px solid #d4a017; padding: 6px; text-align: center; font-size: 11px; }
                        .patient-info-row {
                            display: flex;
                            justify-content: space-between;
                            font-size: 13px;
                            margin-bottom: 5px;
                            border-bottom: 1px dotted #ccc;
                            padding-bottom: 3px;
                        }
                        .patient-info-row div { flex: 1; }
                        .patient-info-row div:last-child { text-align: right; }
                        
                        .footer-signs { 
                            margin-top: auto; /* Push to bottom of the flex container */
                            display: flex; 
                            justify-content: flex-end; 
                            padding-right: 20px;
                            padding-bottom: 20mm;
                        }
                        .sign-box { 
                            text-align: center; 
                            width: 240px; 
                        }
                        .sign-line { 
                            border-top: 1.5px solid #000; 
                            padding-top: 8px; 
                            font-weight: bold; 
                            font-size: 13px; 
                            text-transform: uppercase;
                        }
                        @media print {
                            .no-print { display: none; }
                            body { margin: 0; }
                        }
                    </style>
                </head>
                <body>
                    <div class="page-wrapper">
                        <div class="print-container">
                            ${getHeaderHtml(hospitalData)}
                            <div style="text-align: center; margin-bottom: 20px; text-transform: uppercase;"><strong>TPR Report - Vital Signs History</strong></div>
                            <div style="margin-top: 15px; margin-bottom: 20px;">
                                <div class="patient-info-row">
                                    <div><strong>PATIENT'S NAME:</strong> ${(patientData.name || "").toUpperCase()}</div>
                                    <div><strong>DATE:</strong> ${new Date().toLocaleDateString('en-IN')}</div>
                                </div>
                                <div class="patient-info-row">
                                    <div><strong>AGE / GENDER:</strong> ${patientData.age || "-"} YRS / ${patientData.gender || "-"}</div>
                                    <div><strong>IPD NO:</strong> ${patientData.ipdAdmissionId || "-"}</div>
                                </div>
                            </div>
                            <div style="width: 100%;">
                                ${printContent.innerHTML}
                            </div>
                            <div class="footer-signs">
                                <div class="sign-box">
                                    <div class="sign-line">Authority Sign / Doctor Sign</div>
                                    <div style="font-size: 11px; margin-top: 5px; font-weight: bold;">${hospitalData?.doctor || ""}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </body>
            </html>
        `);
        doc.close();

        // Give it a moment to load images/styles if any
        setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        }, 500);
    };

    const handleExport = () => {
        exportTPRChartPDF(patientData, data, authCtx);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{ backgroundColor: 'white', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '8px', borderBottom: '1px solid #eee' }}>{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color, margin: '2px 0', fontSize: '13px' }}>
                            <strong>{t(entry.name)}:</strong> {entry.value} {entry.unit}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const renderChart = () => {
        if (data.length < 2 && activeMetric !== 'tpr') {
            return <div className="text-center py-5 text-muted">{t("Need at least two data points to show trend.")}</div>
        }

        switch (activeMetric) {
            case 'tpr':
                return (
                    <div id="tpr-printable-section">
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fffbe6' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#fff2ac' }}>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("DATE")}</th>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("TIME")}</th>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("P")}</th>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("BP")}</th>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("TEMP")}</th>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("SPO2")}</th>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("WT")}</th>
                                        <th style={{ border: '1px solid #d4a017', padding: '8px', fontSize: '12px' }}>{t("REMARKS")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{item.date}</td>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'center', fontSize: '11px', fontWeight: 'bold' }}>{item.time}</td>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{item.pulse || "-"}</td>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{item.bpSystolic ? `${item.bpSystolic}/${item.bpDiastolic}` : "-"}</td>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{item.temp || "-"}</td>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{item.spo2 || "-"}</td>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'center', fontSize: '11px' }}>{item.weight || "-"}</td>
                                            <td style={{ border: '1px solid #d4a017', padding: '4px', textAlign: 'left', fontSize: '10px', maxWidth: '300px' }}>{item.notes}</td>
                                        </tr>
                                    ))}
                                    {/* Add empty rows to fill the grid if data is small */}
                                    {data.length < 20 && Array.from({ length: 20 - data.length }).map((_, i) => (
                                        <tr key={`empty-${i}`}>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px', height: '24px' }}></td>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px' }}></td>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px' }}></td>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px' }}></td>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px' }}></td>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px' }}></td>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px' }}></td>
                                            <td style={{ border: '1px solid #d4a017', padding: '12px' }}></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            case 'cardio':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={data}>
                            <defs>
                                <linearGradient id="colorPulse" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#e53e3e" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="#e53e3e" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorSpo2" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3182ce" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="#3182ce" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="formattedTime" interval="preserveStartEnd" style={{ fontSize: '10px' }} />
                            <YAxis yAxisId="pulse" domain={['auto', 'auto']} label={{ value: 'Pulse (bpm)', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }} />
                            <YAxis yAxisId="spo2" orientation="right" domain={[80, 100]} label={{ value: 'SpO2 (%)', angle: 90, position: 'insideRight', style: { fontSize: '10px' } }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend verticalAlign="top" height={36} />
                            <Area yAxisId="pulse" type="monotone" dataKey="pulse" name="Pulse" stroke="#e53e3e" fillOpacity={1} fill="url(#colorPulse)" unit=" bpm" strokeWidth={2} />
                            <Area yAxisId="spo2" type="monotone" dataKey="spo2" name="SpO2" stroke="#3182ce" fillOpacity={1} fill="url(#colorSpo2)" unit="%" strokeWidth={2} />
                            <ReferenceLine yAxisId="spo2" y={90} label="Low SpO2" stroke="red" strokeDasharray="3 3" />
                        </AreaChart>
                    </ResponsiveContainer>
                );
            case 'fever':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="formattedTime" interval="preserveStartEnd" style={{ fontSize: '10px' }} />
                            <YAxis domain={['auto', 'auto']} unit="°F" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend verticalAlign="top" height={36} />
                            <Line type="monotone" dataKey="temp" name="Temperature" stroke="#d69e2e" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} unit="°F" />
                            <ReferenceLine y={98.6} label="Normal" stroke="#48bb78" strokeDasharray="3 3" />
                            <ReferenceLine y={100.4} label="Fever" stroke="#e53e3e" strokeDasharray="3 3" />
                        </LineChart>
                    </ResponsiveContainer>
                );
            case 'bp':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="formattedTime" interval="preserveStartEnd" style={{ fontSize: '10px' }} />
                            <YAxis domain={['auto', 'auto']} unit=" mmHg" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend verticalAlign="top" height={36} />
                            <Line type="monotone" dataKey="bpSystolic" name="Systolic BP" stroke="#319795" strokeWidth={2} dot={{ r: 4 }} unit=" mmHg" />
                            <Line type="monotone" dataKey="bpDiastolic" name="Diastolic BP" stroke="#c53030" strokeWidth={2} dot={{ r: 4 }} unit=" mmHg" />
                            <ReferenceLine y={120} label="Ideal Sys" stroke="#319795" strokeDasharray="3 3" />
                            <ReferenceLine y={80} label="Ideal Dia" stroke="#c53030" strokeDasharray="3 3" />
                        </LineChart>
                    </ResponsiveContainer>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '25px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ padding: '15px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <h5 style={{ margin: 0, fontWeight: '700', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaChartLine /> {t("Patient Vitals Progression")}
                </h5>
                <div style={{ display: 'flex', gap: '4px', backgroundColor: '#e2e8f0', padding: '3px', borderRadius: '8px' }}>
                    <button
                        onClick={() => setActiveMetric('tpr')}
                        style={{
                            padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                            backgroundColor: activeMetric === 'tpr' ? '#fff' : 'transparent',
                            color: activeMetric === 'tpr' ? '#0f172a' : '#64748b',
                            boxShadow: activeMetric === 'tpr' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <FaTable /> {t("TPR")}
                    </button>
                    <button
                        onClick={() => setActiveMetric('cardio')}
                        style={{
                            padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                            backgroundColor: activeMetric === 'cardio' ? '#fff' : 'transparent',
                            color: activeMetric === 'cardio' ? '#0f172a' : '#64748b',
                            boxShadow: activeMetric === 'cardio' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <FaHeartbeat /> {t("Cardio")}
                    </button>
                    <button
                        onClick={() => setActiveMetric('fever')}
                        style={{
                            padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                            backgroundColor: activeMetric === 'fever' ? '#fff' : 'transparent',
                            color: activeMetric === 'fever' ? '#0f172a' : '#64748b',
                            boxShadow: activeMetric === 'fever' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <FaThermometerHalf /> {t("Fever")}
                    </button>
                    <button
                        onClick={() => setActiveMetric('bp')}
                        style={{
                            padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                            backgroundColor: activeMetric === 'bp' ? '#fff' : 'transparent',
                            color: activeMetric === 'bp' ? '#0f172a' : '#64748b',
                            boxShadow: activeMetric === 'bp' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                        }}
                    >
                        <FaTint /> {t("BP")}
                    </button>
                </div>
                {activeMetric === 'tpr' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn-secondary-premium btn-sm d-flex align-items-center gap-2" onClick={handlePrint}>
                            <FaPrint /> {t("Print")}
                        </button>
                        <button className="btn-export-premium btn-sm d-flex align-items-center gap-2" onClick={handleExport}>
                            <FaFilePdf /> {t("Export")}
                        </button>
                    </div>
                )}
            </div>
            <div style={{ padding: '20px' }}>
                {renderChart()}
            </div>
        </div>
    );
};

export default VitalsTrendChart;
